/**
 * Seeding the Broker Details form from a Salesforce import.
 *
 * ⚠️ THE BUG THIS FIXES: the proposal printed the CLIENT's landline as the broker's number.
 *
 * The Salesforce import fills the client fields and writes the tender owner to
 * tender.extn.consultant, but it never touched the Broker Details form. Broker Phone is
 * mandatory, so the user had to put something in an empty box — and the nearest number on the
 * screen is the client's, which they copied. Then handleNext calls saveTenderConsultant AFTER
 * the import flush, so that copied number overwrote the real owner's phone on the tender.
 *
 * Measured on prod: of the 25 most recent tenders carrying both an import record and a stored
 * consultant, 12 had extn.consultant.phone EXACTLY equal to the imported Account.Phone —
 * character for character, formatting and all ("(07) 5594 9844", "+44 7584 433074"). That is not
 * 12 people typing; it is 12 people copying the field above.
 *
 * Reported case, BARRY ENTERPRISES QLD PTY LTD: Salesforce supplied the owner's phone
 * 61489273513, and the tender ended up storing the account's (07) 5478 9811.
 *
 * So the import now seeds the form with the owner it just fetched.
 */

/** What the Broker Details form holds. Mirrors TenderConsultant. */
export type ConsultantFormValues = {
  name: string;
  email: string;
  phone: string;
};

/** The owner fields a Salesforce import carries. Any of them may be null. */
export type SalesforceConsultant = {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  mobile?: string | null;
};

const clean = (value: string | null | undefined): string => (typeof value === 'string' ? value.trim() : '');

/**
 * One number for the one Broker Phone field.
 *
 * Mobile first, exactly as getTenderConsultant resolves the stored record, so the form and the
 * tender never disagree about which of the two Salesforce numbers is the broker's. Most owner
 * records populate only one of them.
 */
export const consultantPhoneFromSalesforce = (sf: SalesforceConsultant): string =>
  clean(sf.mobile) || clean(sf.phone);

/**
 * Merge the imported owner into what the form currently shows.
 *
 * `userEdited` decides who wins:
 *   • false — the box holds a pre-fill (the signed-in user's own profile) or nothing, and the
 *     tender owner Salesforce just named is the better answer, so it replaces it.
 *   • true — the user typed in this form. Their typing is never overwritten; the import only
 *     fills boxes they left empty.
 *
 * A field Salesforce has no value for never blanks what is already there, under either rule.
 */
export const mergeSalesforceConsultant = (
  current: ConsultantFormValues,
  sf: SalesforceConsultant | null | undefined,
  userEdited: boolean,
): ConsultantFormValues => {
  if (!sf) return current;

  const imported: ConsultantFormValues = {
    name: clean(sf.name),
    email: clean(sf.email),
    phone: consultantPhoneFromSalesforce(sf),
  };

  const pick = (field: keyof ConsultantFormValues): string => {
    const existing = clean(current[field]);
    if (!imported[field]) return existing;        // nothing imported — keep what is there
    if (userEdited && existing) return existing;  // the user's own typing stands
    return imported[field];
  };

  return { name: pick('name'), email: pick('email'), phone: pick('phone') };
};
